/*
 * Built-in content seeds for the Event Configuration "Content" tab.
 *
 * These mirror the hardcoded HTML fallbacks that live in the legacy PHP pages
 * (eplus/_clients/<client>/*.php, rendered via renderContentBlock /
 * renderProgramTabs). When a content block has no stored value yet, the editor
 * is pre-filled with the matching seed below so the admin starts from the
 * page's current content instead of an empty editor.
 *
 * Program seeds follow the stored shape consumed by renderProgramTabs():
 * each tab's `html` is the inner content of one tab panel.
 */

export interface ProgramTabSeed {
  title: string;
  html: string;
}

export interface ContentDefaults {
  prose: { [key: string]: string };
  program: { [key: string]: ProgramTabSeed[] };
}

export const CONTENT_DEFAULTS: ContentDefaults = {
  prose: {
    // login.php → renderContentBlock('login_welcome')
    login_welcome: `<h1 class="maintitle">Welcome to Workshop France!</h1>
<p>Hello and welcome to the website dedicated to the registration and scheduling of meeting appointments for the <strong>Workshop France 2024</strong> in Marseille.</p>
<p>You need to be registered and logged on in order to view your personal agenda.</p>`,

    // exexhibitors.php → renderContentBlock('exexhibitors')
    exexhibitors: `<h1 class="maintitle">Chers partenaires,</h1>
<p>Atout France, l'agence française de développement touristique, est très heureuse de vous convier à cette nouvelle édition du Workshop France 2024 - le nouveau rendez-vous de l'offre touristique française pour le marché allemand se tiendra du 25 au 27 septembre 2024 à Marseille.</p>
<p>Venez à la rencontre d'une sélection de 35 à 40 tour-opérateurs et agences de voyages à la fois génériques et spécialisées venues des quatre coins de l'Allemagne. Profitez de deux matinées d'entretiens professionnels (dont une préprogrammée), d'une soirée d'ouverture, d'une seconde soirée plus informelle ainsi qu'un programme proposé par La Ville de Marseille pour vous faire découvrir la destination hôte et partenaire de cette édition.</p>
<p>Nous nous réjouissons de vous accueillir à Marseille dans le cadre de cet événement fédérateur !</p>
<p>L'équipe Promotion Commerciale d'Atout France Allemagne</p>`,

    // vvisitors.php → renderContentBlock('vvisitors')
    vvisitors: `<h1 class="maintitle">Chers partenaires,</h1>
<p>Atout France, l'agence française de développement touristique, est très heureuse de vous convier à ce workshop dédié à l'offre touristique française.</p>
<p>Venez à la rencontre des partenaires français lors de deux journées d'échanges professionnels, comprenant des rendez-vous préprogrammés ainsi qu'un programme de découverte de la destination hôte.</p>
<p>Nous nous réjouissons de vous accueillir dans le cadre de cet événement fédérateur !</p>
<p>L'équipe Promotion Commerciale d'Atout France</p>`,

    // vcontact.php → renderContentBlock('vcontact')
    vcontact: `<h1 class="maintitle">Kontakt</h1>
<p>Für mehrere Informationen, kontaktieren Sie bitte:</p>
<ul role="list" class="-mt-12 space-y-12 divide-y divide-gray-200 xl:col-span-3">
  <li class="flex flex-col gap-10 pt-12 sm:flex-row">
    <img class="aspect-[4/5] w-52 flex-none rounded-2xl object-cover" src="./_fileDB/images/contact/pascal-reiling.jpg" alt="">
    <div class="max-w-xl flex-auto">
      <h3 class="text-lg font-semibold leading-8 tracking-tight text-gray-900">Pascal Reiling</h3>
      <p class="text-base leading-7 text-gray-600">Deutschland</p>
      <ul role="list" class="mt-6">
        <li><a href="mailto:pascal.reiling@atout-france.fr" class="text-gray-400 hover:text-gray-500"><span class="truncate">pascal.reiling@atout-france.fr</span></a></li>
      </ul>
    </div>
  </li>
  <li class="flex flex-col gap-10 pt-12 sm:flex-row">
    <img class="aspect-[4/5] w-52 flex-none rounded-2xl object-cover" src="./_fileDB/images/contact/maude-begue.jpeg" alt="">
    <div class="max-w-xl flex-auto">
      <h3 class="text-lg font-semibold leading-8 tracking-tight text-gray-900">Maude Begue</h3>
      <p class="text-base leading-7 text-gray-600">Deutschland</p>
      <ul role="list" class="mt-6">
        <li><a href="mailto:maude-begue@atout-france.fr" class="text-gray-400 hover:text-gray-500"><span class="truncate">maude-begue@atout-france.fr</span></a></li>
      </ul>
    </div>
  </li>
</ul>`,

    // excontact.php → renderContentBlock('excontact')
    excontact: `<h1 class="maintitle">Contact</h1>
<p>Pour toute information complémentaire, veuillez contacter:</p>
<ul role="list" class="-mt-12 space-y-12 divide-y divide-gray-200 xl:col-span-3">
  <li class="flex flex-col gap-10 pt-12 sm:flex-row">
    <img class="aspect-[4/5] w-52 flex-none rounded-2xl object-cover" src="./_fileDB/images/contact/pascal-reiling.jpg" alt="">
    <div class="max-w-xl flex-auto">
      <h3 class="text-lg font-semibold leading-8 tracking-tight text-gray-900">Pascal Reiling</h3>
      <p class="text-base leading-7 text-gray-600">France</p>
      <ul role="list" class="mt-6">
        <li><a href="mailto:pascal.reiling@atout-france.fr" class="text-gray-400 hover:text-gray-500"><span class="truncate">pascal.reiling@atout-france.fr</span></a></li>
      </ul>
    </div>
  </li>
</ul>
<p>ou</p>
<ul role="list" class="-mt-12 space-y-12 divide-y divide-gray-200 xl:col-span-3">
  <li class="flex flex-col gap-10 pt-12 sm:flex-row">
    <img class="aspect-[4/5] w-52 flex-none rounded-2xl object-cover" src="./_fileDB/images/contact/maude-begue.jpeg" alt="">
    <div class="max-w-xl flex-auto">
      <h3 class="text-lg font-semibold leading-8 tracking-tight text-gray-900">Maude Begue</h3>
      <p class="text-base leading-7 text-gray-600">France</p>
      <ul role="list" class="mt-6">
        <li><a href="mailto:maude-begue@atout-france.fr" class="text-gray-400 hover:text-gray-500"><span class="truncate">maude-begue@atout-france.fr</span></a></li>
      </ul>
    </div>
  </li>
</ul>`,

    // vpartners.php → renderContentBlock('vpartners') (logos come from partners.inc)
    vpartners: `<h1 class="maintitle">Partner</h1>`,

    // expartners.php → renderContentBlock('expartners') (logos come from partners.inc)
    expartners: `<h1 class="maintitle">Partenaires</h1>`,
  },

  program: {
    // vprogram.php → renderProgramTabs('vprogram')
    vprogram: [
      {
        title: '24.09.2024',
        html: `<h2 class="pt-0">Dienstag, 24 September 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">Abend</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Ankunft in Marseille mit dem Zug oder Flug.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Transfer und Check-in im <a href="https://www.ihg.com/crowneplaza/hotels/de/de/marseille/mrsdm/hoteldetail" target="_blank">Hotel Crowne Plaza: 12/14 Rue Sainte-Adélaïde, 13004 Marseille, Frankreich.</a></p>
  </li>
</ol>`,
      },
      {
        title: '25.09.2024',
        html: `<h2 class="pt-0">Mittwoch, 25. September 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Frühstück im Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Letzte Ankünfte aus Deutschland</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">09:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Transfer mit der Métro ins Stadtzentrum. Zu Fuß geht es über den Fischmarkt am Vieux Port. Besuch eines 4 Sterne Hotels und der Grotte Cosquer Méditerranée.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">13:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Einschiffung auf einem Katamaran im Hafenbecken des Mucem, mit den französischen Partnern, um die Bucht von Marseille zu erkunden. Mittagscocktail an Bord in einer Bucht der Frioul-Inseln.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">16:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Rückkehr zum Mucem und Bustransfer zum Hotel. Zeit zur freien Verfügung.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">18:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Abfahrt vom Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">19:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Ankunft in der Eventlocation Boa Vista<br>Aperitif und Begrüßungsworte<br>Gesetztes Dinner in Strand- und Loungeatmosphäre des Boa Vista<br>Dresscode: Casual Chic</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">22:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Rückkehr zum Hotel</p>
  </li>
</ol>`,
      },
      {
        title: '26.09.2024',
        html: `<h2 class="pt-0">Donnerstag, 26 September 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Frühstück im Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">09:00 – 12:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">20-minütige Speed-Meetings im Crowne Plaza Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">12:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Bustransfer</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">13:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Mittagessen in den Halles de la Major &amp; Briefing für die Fußgängerrallye</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">14:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Besuch des Mucem, Musée National &amp; Fußgängerrallye durch das Viertel Panier.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">17:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Rückkehr im Vieux Port (Métro Haltestelle - Linie M1) Transfer in den Gruppen mit dem Métro (bis Haltestelle Chartreux) zum Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">19:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Abfahrt im Bus in Richtung der Docks zum Mx Expérience für die Soirée und Auflösung der Rallye</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">22:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Bustransfer zum Hotel (1. Option)</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">23:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Bustransfer zum Hotel (2. Option)</p>
  </li>
</ol>`,
      },
      {
        title: '27.09.2024',
        html: `<h2 class="pt-0">Freitag, 27 September 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Frühstück im Hotel, Check-out</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">09:00 – 12:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">20-minütige Speed-Meetings im Crowne Plaza Hotel</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">12:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Mittagessen im Crowne Plaza Restaurant</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">14:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Start der Inforeisen (Optional) / Transfer zum Bahnhof oder Flughafen</p>
  </li>
</ol>`,
      },
    ],

    // exprogram.php → renderProgramTabs('exprogram')
    exprogram: [
      {
        title: '25.09.2024',
        html: `<h2 class="pt-0">Mercredi, 25 Septembre 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Arrivée individuelle de tous les partenaires avant 18H à <a href="https://www.ihg.com/crowneplaza/hotels/fr/fr/marseille/mrsdm/hoteldetail" target="_blank">l'hôtel Crowne Plaza : 12/14 Rue Sainte-Adélaïde, 13004 Marseille, France</a></p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Check-in</p>
  </li>
  <li class="py-4">
    <p class="text-blue-600 font-semibold">Facultatif:</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Avant 12H – Arrivée à l'hôtel pour ceux qui souhaitent participer à l'activité, Check-in</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">12:15</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Départ de l'hôtel vers le Vieux Port.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">13:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Embarquement sur un catamaran sur le Quai du Port (proche MUCEM) pour découvrir la rade de Marseille. Cocktail déjeunatoire à bord dans la Baie des Îles du Frioul.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">16:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Retour au port et transfert vers l'hôtel en bus. Temps libre.</p>
  </li>
  <li class="py-4">
    <p class="text-blue-600 font-semibold">Obligatoire:</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">18:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Départ en bus de l'hôtel.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">19:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Arrivée au Boa Vista. Apéritif et mots de bienvenue<br>Dîner assis en bord de plage dans une atmosphère lounge<br>Dresscode: Casual Chic</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">22:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Retour à l'hôtel.</p>
  </li>
</ol>`,
      },
      {
        title: '26.09.2024',
        html: `<h2 class="pt-0">Jeudi, 26 Septembre 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Petit-déjeuner</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">08:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Préparation des exposants dans la salle de Workshop.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">09:00 – 12:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Workshop avec rendez-vous préprogrammés de 20 minutes.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">12:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Transfert en bus.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">13:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Déjeuner dans les Halles de la Major et Briefing du Rallye pédestre.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">14:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Visite du MUCEM, Musée National &amp; Rallye pédestre dans le quartier Panier.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">17:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Retour à l'hôtel depuis l'arrêt de métro du vieux Port (ligne M1) puis arrêt à Chartreux proche de l'hôtel.<br>Temps libre.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">19:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Départ en bus en direction des Docks au Mx Expérience pour la soirée et remise des prix du Rallye pédestre.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">22:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Retour à l'hôtel en bus (1ère navette)</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">23:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Retour à l'hôtel en bus (2ème navette)</p>
  </li>
</ol>`,
      },
      {
        title: '27.09.2024',
        html: `<h2 class="pt-0">Vendredi, 27 Septembre 2024</h2>
<ol class="mt-2 divide-y divide-gray-200 text-sm leading-6 text-gray-500">
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">&nbsp;</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Petit-déjeuner et check-out</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">08:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Préparation des exposants dans la salle de Workshop.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">09:00 – 12:00</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Workshop avec rendez-vous préprogrammés de 20 minutes.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">12:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Cocktail déjeunatoire au restaurant de l'hôtel.</p>
  </li>
  <li class="py-4 sm:flex">
    <time class="w-28 flex-none">14:30</time>
    <p class="flex-auto font-semibold text-gray-900 sm:mt-0">Départ des exposants.</p>
  </li>
</ol>`,
      },
    ],
  },
};
